/**
 * Analytics for the partner portal (FR-I-4).
 *
 * This app had NONE. Not a broken tag or a wrong id — no analytics at all,
 * while `apps/go` and the marketplace both push to the same GTM container. So
 * every question about the portal ("do partners who add a draft ever send it?")
 * has been unanswerable, which is the question FR-I-4 exists to answer.
 *
 * GA4 IS DELIVERED THROUGH GTM, never by a second gtag.js loader. The page's
 * only job is to push well-named, PII-free events onto `dataLayer`; the GA4
 * configuration tag lives in the container. Loading GA4 here as well would
 * double-count every view.
 *
 * THE `environment` PUSH IS NOT OPTIONAL. The container's GA4 config tag reads
 * its measurement id from a lookup keyed on a dataLayer variable named
 * `environment`, with NO DEFAULT. `apps/go` shipped for months with GTM loaded
 * and that push missing: the lookup returned undefined, the config tag had no
 * destination, and nothing was sent. Both halves live in `initAnalytics` for
 * that reason — fixing either alone changes nothing.
 *
 * Every name here must also exist as a trigger in GTM or it goes nowhere. See
 * docs/operations/gtm-partner-page.md.
 */

import {
	type AnalyticsParams,
	buildEvent,
} from "@interioring/utils/analytics/data-layer";

/**
 * The vocabulary. Kept local rather than shared with `apps/go`: that app fires
 * campaign-page events and this one fires dashboard events, and a merged list
 * would offer each app names its container has no trigger for.
 */
export const PARTNER_EVENTS = {
	/** Once per session, after the dashboard first paints with real data. */
	dashboardView: "partner_dashboard_view",
	/** They tapped the one prompt the action strip decided to show (FR-P-2.4). */
	actionTap: "partner_action_tap",
	actionDismiss: "partner_action_dismiss",
	/**
	 * The referral funnel, from this side of it.
	 *
	 * There is deliberately no `referral_start`. The form is one screen with no
	 * step to abandon halfway, so "started" and "opened /refer" would be the
	 * same event under two names — and a defined event nobody fires is the
	 * orphan this file exists to avoid, at a smaller scale.
	 */
	referralCreated: "partner_referral_created",
	referralForwarded: "partner_referral_forwarded",
	/** Money. Never carries an amount — see below. */
	payoutRequested: "partner_payout_requested",
	ledgerDownloaded: "partner_ledger_downloaded",
	helpOpened: "partner_help_opened",
} as const;

export type PartnerEvent = (typeof PARTNER_EVENTS)[keyof typeof PARTNER_EVENTS];

/**
 * The GTM container, version-controlled rather than left to an env variable.
 *
 * Same reasoning as `apps/go/src/lib/campaign.ts`, and the same history: that
 * app's id previously lived only in a CI variable that was never set, so v0
 * shipped with no analytics and no build ever complained. This value renders
 * into public HTML — it is not a secret — so it belongs in a diff.
 *
 * It is the SAME container as the campaign pages. One container, one GA4
 * property: a partner who taps a `go.interioring.com` link and then signs in
 * here is one journey, and splitting it across two containers would make that
 * unanswerable.
 */
export const PARTNER_GTM_CONTAINER_ID = "GTM-K6J94X7R";

/**
 * The container to load, if any.
 *
 * Only in deployed environments. Local development pushing into the live
 * container would put a developer's clicking through every funnel the team
 * reads, and `VITE_ENVIRONMENT` is unset locally, so the default is off.
 */
export function containerFor(
	override: string | undefined,
	environment: string | undefined,
): string {
	if (override) return override;
	return environment ? PARTNER_GTM_CONTAINER_ID : "";
}

declare global {
	interface Window {
		dataLayer?: Record<string, unknown>[];
	}
}

/**
 * Push one event.
 *
 * NO AMOUNTS, EVER. A payout event carrying `amountPaise` is one partner's
 * earnings in a third party's analytics, and with a session it is attributable
 * to a person. The denylist in `@interioring/utils` catches identifiers; this
 * comment is what catches the amount, because "how much" is a legitimate-
 * looking metric and nothing mechanical will stop it.
 *
 * Never throws. Analytics failing must never take a partner's balance down
 * with it.
 */
export function track(event: PartnerEvent, params: AnalyticsParams = {}): void {
	try {
		const payload = buildEvent(event, params, (keys) => {
			if (import.meta.env.DEV) {
				console.warn(
					`[analytics] refused ${event}: ${keys.join(", ")} looks like personal data`,
				);
			}
		});
		if (!payload) return;
		window.dataLayer = window.dataLayer ?? [];
		window.dataLayer.push(payload);
	} catch {
		// A blocked dataLayer, an extension, a sandboxed frame. Nothing here is
		// worth a broken screen.
	}
}

/**
 * Load GTM and push the environment the container keys GA4 on.
 *
 * Call ONCE, before anything that tracks. A missing container id is a no-op
 * rather than an error: local development has no container, and refusing to
 * boot the app over analytics would be the wrong trade every time.
 */
export function initAnalytics(
	containerId: string | undefined,
	rawEnvironment: string | undefined,
): void {
	if (!containerId) return;
	try {
		// deploy.yml emits "prod" while wrangler says "production"; the lookup
		// table only knows "production", so normalise rather than hope.
		const raw = rawEnvironment ?? "local";
		const environment = raw === "prod" ? "production" : raw;

		window.dataLayer = window.dataLayer ?? [];
		// BEFORE the container loads, so the config tag can read it on the very
		// first evaluation rather than on a later push it may already have missed.
		window.dataLayer.push({ environment });
		window.dataLayer.push({
			"gtm.start": Date.now(),
			event: "gtm.js",
		});

		const script = document.createElement("script");
		script.async = true;
		script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
		document.head.appendChild(script);
	} catch {
		// Same reasoning as `track`.
	}
}
